<template>
  <div class="tw-relative tw-justify-center md:tw-sticky md:tw-top-0">
    <PropertiesSearchFilterModal
      :bathroom-count="parseInt(selectedBathrooms, 10)"
      :current-max-price="selectedMaxPrice"
      :current-min-price="minCheapestRoomPrice"
      :current-enquired-properties="currentEnquiredProperties"
      :room-count="parseInt(selectedBedrooms, 10)"
      :show="showModal"
      :use-exact-room-counts="useExactRoomCounts"
      :enable-student-enquiries-cta="enableStudentEnquiriesCta"
      @close="handleModalClose"
      @set-max-price="handleMaxPriceChange"
      @set-current-bedrooms="handleCurrentBedroomsChange"
      @set-current-bathrooms="handleCurrentBathroomsChange"
    />
    <ExpandedPropertiesSearchFilterModal
      :show="showFilterModal"
      :current-university="currentUniversity"
      :current-area="currentArea"
      :universities="allUniversities"
      :current-property-type="currentType"
      :current-expanded-properties="currentExpandedProperties"
      :current-enquired-properties="currentEnquiredProperties"
      :areas="areas"
      :enable-property-type-filters="enablePropertyTypeFilters"
      :enable-private-halls-filter="enablePrivateHallsFilter"
      :enable-spare-room-functionality="enableSpareRoomFunctionality"
      :enable-student-enquiries-cta="enableStudentEnquiriesCta"
      :has-enquired-properties="hasEnquiredProperties"
      @close="handleFilterModalClose"
      @set-property-type="handlePropertyTypeChange"
      @set-university="handleUniversitySelection"
      @set-area="handleAreaSelection"
      @set-enquired-properties="handleEnquiredPropertiesChange"
    />

    <MobileSearchStackedFiltersModal
      :show="showMobileModal"
      :current-area="currentArea"
      :areas="areas"
      :current-university="currentUniversity"
      :current-bedrooms="currentBedrooms"
      :current-bathrooms="currentBathrooms"
      :current-type="currentTypeAsString"
      :current-enquired-properties="currentEnquiredProperties"
      :universities="allUniversities"
      :enable-private-halls-filter="enablePrivateHallsFilter"
      :enable-spare-room-functionality="enableSpareRoomFunctionality"
      :enable-local-filter-counts="enableLocalFilterCounts"
      :enable-student-enquiries-cta="enableStudentEnquiriesCta"
      :use-exact-room-counts="useExactRoomCounts"
      :has-enquired-properties="hasEnquiredProperties"
      @close="handleMobileModalClose"
      @set-min-price="handleMinPriceChange"
      @set-max-price="handleMaxPriceChange"
      @set-current-bedrooms="handleCurrentBedroomsChange"
      @set-current-bathrooms="handleCurrentBathroomsChange"
      @set-property-type="handlePropertyTypeChange"
      @set-area="handleAreaSelection"
      @set-university="handleUniversitySelection"
      @set-enquired-properties="handleEnquiredPropertiesChange"
    />

    <div
      class="contained tw-w-full tw-bg-white tw-py-3 lg:tw-hidden"
      :class="{
        'tw-pointer-events-none tw-select-none tw-grayscale lg:tw-opacity-40':
          studentStore.isHousemateGroupManageMode ||
          studentStore.disableFilters,
      }"
      :aria-disabled="
        studentStore.isHousemateGroupManageMode || studentStore.disableFilters
          ? 'true'
          : 'false'
      "
    >
      <slot name="shortlist-list-selector-mobile" />
      <div class="tw-flex tw-items-center tw-justify-between">
        <div>
          <div class="tw-flex tw-items-center tw-space-x-1">
            <button
              v-if="mapStore.mapIsOpen && canShowMapView"
              type="button"
              class="tw-rounded-[25px] tw-border tw-p-[13px] hover:tw-bg-gray-100"
              @click="$emit('toggle-map-is-open')"
            >
              <img
                :src="cdnAsset('svg/listings/uh-cross.svg')"
                alt="Close"
                class="tw-h-[24px] tw-w-[24px]"
              />
            </button>
            <div v-if="!studentStore.showSearchSubMenu">
              <div class="tw-flex tw-gap-1">
                <span
                  v-if="currentEnquiredProperties"
                  class="tw-text-md tw-text-trueGray-700 tw-font-semibold"
                >
                  Enquiries
                </span>
                <img
                  v-else
                  :src="cdnAsset('svg/listings/location-pin-grey.svg')"
                  alt="Location Pin"
                  class="tw-h-[22px] tw-w-[22px]"
                />
                <span
                  v-if="currentCity && !currentEnquiredProperties"
                  class="tw-text-md tw-text-trueGray-700 tw-font-semibold"
                >
                  {{
                    currentUniversity?.name ||
                    currentArea?.name ||
                    currentCity.name
                  }}
                </span>
              </div>
              <div
                class="tw-text-trueGray-600 tw-mt-[8px] tw-pl-[2px] tw-text-sm"
              >
                <span v-if="resultsCountLoading">Loading results</span>
                <span v-else>
                  <strong>{{ propertyCount }}</strong>
                  {{ propertyCount === 1 ? 'result' : 'results' }}
                </span>
                <span v-if="currentCity">in {{ currentCity.name }}</span>
              </div>
            </div>

            <!-- Sub Menu Information -->
            <div v-if="studentStore.showSearchSubMenu">
              <div class="tw-flex tw-gap-1">
                <span
                  v-if="studentStore.searchSubMenuHeaderText"
                  class="tw-text-md tw-text-trueGray-700 tw-h-[22px] tw-font-semibold"
                >
                  {{ studentStore.searchSubMenuHeaderText }}
                </span>
              </div>
              <div
                class="tw-text-trueGray-600 tw-mt-[8px] tw-pl-[2px] tw-text-sm"
              >
                <span>
                  <strong>{{ studentStore.searchSubMenuHeaderCount }}</strong>
                  {{
                    studentStore.searchSubMenuHeaderCount === 1
                      ? 'result'
                      : 'results'
                  }}
                </span>
              </div>
            </div>
          </div>
        </div>
        <div
          class="tw-flex tw-items-center tw-gap-2"
          :class="{
            'tw-opacity-40 lg:tw-opacity-[unset]':
              studentStore.isHousemateGroupManageMode ||
              studentStore.disableFilters,
          }"
        >
          <button
            v-if="
              shortlistModeActive &&
              propertyCount > 0 &&
              !mapStore.mapIsOpen &&
              canShowMapView
            "
            type="button"
            class="tw-rounded-[25px] tw-border tw-p-[13px] hover:tw-bg-gray-100 md:tw-hidden"
            @click="openMapAndScroll()"
          >
            <img
              :src="cdnAsset('svg/uh-view-map-icon.svg')"
              alt="Map View"
              class="tw-h-[24px] tw-w-[24px]"
            />
          </button>
          <button
            v-if="!mapStore.mapIsOpen && canShowMapView && !canShowGridSplitMaps"
            type="button"
            class="tw-hidden tw-rounded-[25px] tw-border tw-p-[13px] hover:tw-bg-gray-100 md:tw-block"
            @click="openMapAndScroll()"
          >
            <img
              :src="cdnAsset('svg/uh-view-map-icon.svg')"
              alt="Map View"
              class="tw-h-[24px] tw-w-[24px]"
            />
          </button>
          <button
            v-if="!studentStore.disableFilters"
            type="button"
            :class="[
              'tw-rounded-full tw-border tw-p-3 hover:tw-bg-gray-100',
              {
                'filter-button-with-count': appliedFiltersCount > 0,
                disabled: studentStore.disableFilters,
              },
            ]"
            :data-filter-count="appliedFiltersCount"
            data-testid="mobile-more-filters"
            @click="handleMobileModalOpen"
          >
            <img
              :src="cdnAsset('svg/listings/sliders.svg')"
              alt="Price icon"
              class="tw-h-6 tw-w-6 lg:tw-mr-2"
            />
          </button>
          <div
            v-else
            class="tw-flex tw-h-[50px] tw-w-[50px] tw-items-center tw-justify-center tw-rounded-[25px] tw-bg-[#9CA3AF] tw-p-[13px]"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
            >
              <path
                d="M6.00002 9.84375V3.75C6.00002 3.55109 5.92101 3.36032 5.78035 3.21967C5.6397 3.07902 5.44894 3 5.25002 3C5.05111 3 4.86035 3.07902 4.71969 3.21967C4.57904 3.36032 4.50002 3.55109 4.50002 3.75V9.84375C3.85471 10.009 3.28274 10.3843 2.87429 10.9105C2.46584 11.4367 2.24414 12.0839 2.24414 12.75C2.24414 13.4161 2.46584 14.0633 2.87429 14.5895C3.28274 15.1157 3.85471 15.491 4.50002 15.6562V20.25C4.50002 20.4489 4.57904 20.6397 4.71969 20.7803C4.86035 20.921 5.05111 21 5.25002 21C5.44894 21 5.6397 20.921 5.78035 20.7803C5.92101 20.6397 6.00002 20.4489 6.00002 20.25V15.6562C6.64533 15.491 7.2173 15.1157 7.62575 14.5895C8.0342 14.0633 8.25591 13.4161 8.25591 12.75C8.25591 12.0839 8.0342 11.4367 7.62575 10.9105C7.2173 10.3843 6.64533 10.009 6.00002 9.84375ZM5.25002 14.25C4.95335 14.25 4.66334 14.162 4.41667 13.9972C4.16999 13.8324 3.97774 13.5981 3.8642 13.324C3.75067 13.0499 3.72097 12.7483 3.77885 12.4574C3.83672 12.1664 3.97958 11.8991 4.18936 11.6893C4.39914 11.4796 4.66642 11.3367 4.95739 11.2788C5.24836 11.2209 5.54996 11.2506 5.82405 11.3642C6.09814 11.4777 6.33241 11.67 6.49723 11.9166C6.66205 12.1633 6.75002 12.4533 6.75002 12.75C6.75002 13.1478 6.59199 13.5294 6.31068 13.8107C6.02938 14.092 5.64785 14.25 5.25002 14.25ZM12.75 5.34375V3.75C12.75 3.55109 12.671 3.36032 12.5304 3.21967C12.3897 3.07902 12.1989 3 12 3C11.8011 3 11.6103 3.07902 11.4697 3.21967C11.329 3.36032 11.25 3.55109 11.25 3.75V5.34375C10.6047 5.50898 10.0327 5.88428 9.62429 6.41048C9.21584 6.93669 8.99414 7.58387 8.99414 8.25C8.99414 8.91613 9.21584 9.56331 9.62429 10.0895C10.0327 10.6157 10.6047 10.991 11.25 11.1562V20.25C11.25 20.4489 11.329 20.6397 11.4697 20.7803C11.6103 20.921 11.8011 21 12 21C12.1989 21 12.3897 20.921 12.5304 20.7803C12.671 20.6397 12.75 20.4489 12.75 20.25V11.1562C13.3953 10.991 13.9673 10.6157 14.3758 10.0895C14.7842 9.56331 15.0059 8.91613 15.0059 8.25C15.0059 7.58387 14.7842 6.93669 14.3758 6.41048C13.9673 5.88428 13.3953 5.50898 12.75 5.34375ZM12 9.75C11.7034 9.75 11.4133 9.66203 11.1667 9.4972C10.92 9.33238 10.7277 9.09811 10.6142 8.82402C10.5007 8.54994 10.471 8.24834 10.5288 7.95736C10.5867 7.66639 10.7296 7.39912 10.9394 7.18934C11.1491 6.97956 11.4164 6.8367 11.7074 6.77882C11.9984 6.72094 12.3 6.75065 12.574 6.86418C12.8481 6.97771 13.0824 7.16997 13.2472 7.41664C13.412 7.66332 13.5 7.95333 13.5 8.25C13.5 8.64782 13.342 9.02936 13.0607 9.31066C12.7794 9.59196 12.3978 9.75 12 9.75ZM21.75 15.75C21.7494 15.0849 21.5282 14.4388 21.121 13.9129C20.7139 13.387 20.1438 13.011 19.5 12.8438V3.75C19.5 3.55109 19.421 3.36032 19.2804 3.21967C19.1397 3.07902 18.9489 3 18.75 3C18.5511 3 18.3603 3.07902 18.2197 3.21967C18.079 3.36032 18 3.55109 18 3.75V12.8438C17.3547 13.009 16.7827 13.3843 16.3743 13.9105C15.9658 14.4367 15.7441 15.0839 15.7441 15.75C15.7441 16.4161 15.9658 17.0633 16.3743 17.5895C16.7827 18.1157 17.3547 18.491 18 18.6562V20.25C18 20.4489 18.079 20.6397 18.2197 20.7803C18.3603 20.921 18.5511 21 18.75 21C18.9489 21 19.1397 20.921 19.2804 20.7803C19.421 20.6397 19.5 20.4489 19.5 20.25V18.6562C20.1438 18.489 20.7139 18.113 21.121 17.5871C21.5282 17.0612 21.7494 16.4151 21.75 15.75ZM18.75 17.25C18.4534 17.25 18.1633 17.162 17.9167 16.9972C17.67 16.8324 17.4777 16.5981 17.3642 16.324C17.2507 16.0499 17.221 15.7483 17.2788 15.4574C17.3367 15.1664 17.4796 14.8991 17.6894 14.6893C17.8991 14.4796 18.1664 14.3367 18.4574 14.2788C18.7484 14.2209 19.05 14.2506 19.324 14.3642C19.5981 14.4777 19.8324 14.67 19.9972 14.9166C20.162 15.1633 20.25 15.4533 20.25 15.75C20.25 16.1478 20.092 16.5294 19.8107 16.8107C19.5294 17.092 19.1478 17.25 18.75 17.25Z"
                fill="white"
              />
            </svg>
          </div>
        </div>
      </div>
    </div>

    <div
      id="search-element"
      class="container-fluid contained tw-mt-3 tw-hidden tw-h-20 tw-flex-col tw-items-center tw-gap-2 tw-bg-white lg:tw-mb-0 lg:tw-flex lg:tw-flex-row lg:tw-justify-start lg:tw-gap-[16px] lg:tw-px-0"
      :class="[
        enablePropertyTypeFilters
          ? 'lg:tw-mt-0 lg:tw-h-[100px]'
          : 'lg:tw-mt-4 lg:tw-h-24',
        'tw-max-w-screen-xl',
      ]"
    >
      <!-- Close Map Button / Back to results (Grid/Split full-page map) -->
      <button
        v-if="mapStore.mapIsOpen && canShowMapView"
        type="button"
        data-testid="marketplace-map-back-to-results"
        :class="[
          'tw-border-coolGray-300 tw-flex tw-items-center tw-justify-center tw-gap-2 tw-rounded-full tw-border-2 tw-bg-white tw-px-4',
          desktopSearchInputHeightClasses,
          desktopFilterAccentBorderClasses,
        ]"
        @click="$emit('toggle-map-is-open')"
      >
        <img
          :src="cdnAsset('svg/arrow-icon-blue.svg')"
          alt="Back"
          class="tw-h-[24px] tw-w-[24px]"
        />
        <span class="tw-text-xs tw-font-semibold lg:tw-text-sm">{{
          canShowGridSplitMaps ? 'Back to results' : 'List view'
        }}</span>
      </button>
      <!-- location input filter -->
      <div
        v-if="!shortlistModeActive"
        ref="searchLocationInputFilter"
        :class="[
          'tw-border-coolGray-300 tw-flex tw-h-10 tw-flex-row tw-items-center tw-rounded-full tw-border-2 lg:tw-px-2.5',
          desktopSearchInputHeightClasses,
          mapStore.mapIsOpen
            ? 'tw-w-full lg:tw-flex-1'
            : 'lg:tw-min-w-0 tw-w-full lg:tw-flex-1',
          studentStore.isHousemateGroupManageMode || studentStore.disableFilters
            ? 'tw-cursor-not-allowed tw-bg-gray-200'
            : ['tw-bg-white', desktopFilterAccentBorderClasses],
        ]"
        @focusout="handleUniversityHide($event)"
        @focusin="handleUniversityFocus"
      >
        <img
          :src="cdnAsset('svg/listings/location-pin.svg')"
          alt="Location Pin"
          class="tw-ml-2 tw-h-[24px] tw-w-[24px]"
          :class="{
            'tw-grayscale':
              studentStore.isHousemateGroupManageMode ||
              studentStore.disableFilters,
          }"
        />
        <input
          :value="currentUniversity ? currentUniversity.name : ''"
          placeholder="All Universities"
          type="text"
          readonly
          :class="{
            'focus:tw-border-primary-500 focus:tw-ring-primary-500 focus:tw-duration-100':
              shouldShowAreaDropdown,
            'tw-cursor-not-allowed':
              studentStore.isHousemateGroupManageMode ||
              studentStore.disableFilters,
          }"
          :style="
            studentStore.isHousemateGroupManageMode ||
            studentStore.disableFilters
              ? { backgroundColor: '#e5e7eb !important' }
              : {}
          "
          :disabled="
            studentStore.isHousemateGroupManageMode ||
            studentStore.disableFilters
          "
          class="tw-mr-2 tw-grow-[0.8] tw-border-none tw-py-4 tw-pb-1 tw-pt-1.5 tw-align-middle tw-text-sm tw-font-normal tw-text-[#5C6A75] tw-caret-transparent tw-outline-none tw-ring-0 tw-ring-offset-transparent md:tw-font-semibold"
          @click="shouldShowAreaDropdown = true"
        />
        <div
          :style="computedsearchFilterWidthStyle"
          :class="{
            'tw-block': shouldShowAreaDropdown,
            'tw-hidden': !shouldShowAreaDropdown,
          }"
          class="search-filter-select-refresh tw-absolute tw-z-40"
        >
          <div
            class="search-filter-select-refresh-inner hide-scrollbar bg-white tw-absolute tw-top-[30px] tw-ml-[-12px] tw-max-h-[50vh] tw-w-full tw-overflow-y-auto tw-rounded-[16px] tw-border tw-border-[#E5E7EB] tw-drop-shadow-md"
          >
            <div
              v-for="(university, index) in allUniversities"
              :key="`uni-${index}`"
              :class="[
                'search-result-item tw-flex tw-cursor-pointer tw-flex-row tw-gap-3 tw-border-b-[1px] tw-border-[#E5E7EB] tw-px-[24px] tw-py-[16px] tw-text-primary-grey first:tw-rounded-t-[16px] last:tw-rounded-b-[16px] hover:tw-text-white',
                desktopFilterAccentDropdownClasses,
                {
                  'tw-border-b-[1px] tw-border-[#E5E7EB]':
                    allUniversities.length > 0,
                },
              ]"
              @mousedown="handleUniversitySelection(university)"
            >
              <div class="tw-flex tw-flex-row tw-items-center tw-gap-2">
                <span
                  class="bank-icon tw-ml-[8px] tw-h-[24px] tw-w-[20px]"
                ></span>
                <span class="tw-text-[16px] tw-leading-[24px]">{{
                  university.name
                }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
      <a
        v-if="shortlistModeActive"
        :href="shortlistReturnUrl"
        class="tw-border-coolGray-300 tw-flex tw-h-10 tw-items-center tw-justify-center tw-gap-2 tw-rounded-full tw-border-2 tw-bg-white tw-px-4 tw-text-sm tw-font-semibold tw-text-[#374151] hover:tw-text-[#0082B8]"
        :class="desktopSearchInputHeightClasses"
      >
        <img
          :src="cdnAsset('svg/arrow-icon-blue.svg')"
          alt=""
          class="tw-h-5 tw-w-5"
        />
        <span>Back</span>
      </a>
      <slot name="shortlist-list-selector-desktop" />
      <!-- Mobile filters -->
      <div
        class="tw-flex tw-w-full tw-flex-row tw-justify-between tw-px-1 hover:tw-cursor-pointer lg:tw-hidden"
      >
        <div
          class="tw-flex tw-flex-row tw-content-center tw-items-center tw-gap-2"
          @click="handleMobileModalOpen"
        >
          <img
            :src="cdnAsset('svg/new-bed-icon.svg')"
            alt="Bed icon"
            class="tw-h-6 tw-w-6 lg:tw-mr-2"
          />
          <span class="tw-text-sm tw-font-normal xl:tw-text-sm">{{
            displayFilter(selectedBedrooms, 'Any')
          }}</span>
        </div>

        <div
          class="tw-flex tw-flex-row tw-content-center tw-items-center tw-gap-2 hover:tw-cursor-pointer"
          @click="handleMobileModalOpen"
        >
          <img
            :src="cdnAsset('svg/new-coin-icon.svg')"
            alt="Price icon"
            class="tw-h-6 tw-w-6 lg:tw-mr-2"
          />
          <span class="tw-text-sm tw-font-normal xl:tw-text-sm"
            >{{ displayPrice === 'Any' ? '' : 'Max' }} {{ displayPrice }}
            {{ displayPrice === 'Any' ? '' : 'pppw' }}</span
          >
        </div>

        <div
          class="tw-flex tw-flex-row tw-content-center tw-items-center hover:tw-cursor-pointer md:tw-gap-1"
          @click="handleMobileModalOpen"
        >
          <img
            :src="cdnAsset('svg/listings/sliders.svg')"
            alt="Price icon"
            class="tw-h-6 tw-w-6 lg:tw-mr-2"
          />

          <img
            :src="cdnAsset('svg/listings/caret-right.svg')"
            alt="Price icon"
            class="tw-h-3 tw-w-3 lg:tw-mr-2"
          />
        </div>
      </div>
      <!-- Desktop Filters -->
      <PropertyTypeFiltersTrigger
        v-if="enablePropertyTypeFilters"
        :expanded="propertyTypeFiltersExpanded"
        :disabled="
          studentStore.isHousemateGroupManageMode || studentStore.disableFilters
        "
        class="tw-hidden tw-shrink-0 lg:tw-ml-auto lg:tw-flex"
        @toggle="propertyTypeFiltersExpanded = !propertyTypeFiltersExpanded"
      />
      <div
        class="tw-hidden tw-flex-row tw-gap-[16px] lg:tw-flex"
      >
        <div
          class="tw-flex tw-w-[319px] tw-flex-row tw-items-center tw-justify-around tw-rounded-full tw-border-2 tw-border-gray-200 tw-px-2.5"
          :class="[
            desktopSearchInputHeightClasses,
            studentStore.isHousemateGroupManageMode ||
            studentStore.disableFilters
              ? 'tw-cursor-not-allowed tw-bg-gray-200'
              : [
                  'tw-cursor-pointer tw-bg-white',
                  desktopFilterAccentBorderClasses,
                ],
          ]"
          dusk="desktop-property-filters"
          data-testid="desktop-property-filters"
          @click="
            !(
              studentStore.isHousemateGroupManageMode ||
              studentStore.disableFilters
            ) && handleModalOpen()
          "
        >
          <div
            class="tw-flex tw-flex-col tw-content-center tw-items-center lg:tw-flex-row"
          >
            <img
              :src="cdnAsset('svg/uh-bed-icon.svg')"
              alt="Bed icon"
              class="tw-h-4 tw-w-4 lg:tw-mr-2"
              :class="{
                'tw-grayscale':
                  studentStore.isHousemateGroupManageMode ||
                  studentStore.disableFilters,
              }"
            />
            <span
              class="tw-text-xs tw-font-semibold xl:tw-text-sm"
              :class="{
                'tw-text-[#5C6A75]':
                  studentStore.isHousemateGroupManageMode ||
                  studentStore.disableFilters,
              }"
              >{{ displayFilter(selectedBedrooms, 'Any') }}</span
            >
          </div>

          <div class="tw-h-6 tw-w-px tw-bg-gray-300" />

          <div
            class="tw-flex tw-flex-col tw-content-center tw-items-center lg:tw-flex-row"
          >
            <img
              :src="cdnAsset('svg/listings/shower.svg')"
              alt="Shower and Bathroom icon"
              class="tw-h-4 tw-w-4 lg:tw-mr-2"
              :class="{
                'tw-grayscale':
                  studentStore.isHousemateGroupManageMode ||
                  studentStore.disableFilters,
              }"
            />
            <span
              class="tw-text-xs tw-font-semibold xl:tw-text-sm"
              :class="{
                'tw-text-[#5C6A75]':
                  studentStore.isHousemateGroupManageMode ||
                  studentStore.disableFilters,
              }"
              >{{
                useExactRoomCounts
                  ? displayFilter(selectedBathrooms, 'Any')
                  : displayFilter(selectedBathrooms, 'Any', '+')
              }}</span
            >
          </div>

          <div class="tw-h-6 tw-w-px tw-bg-gray-300" />

          <div class="tw-flex tw-flex-row tw-content-center tw-items-center">
            <img
              :src="cdnAsset('svg/listings/uh-coins.svg')"
              alt="Price icon"
              class="tw-h-4 tw-w-4 lg:tw-mr-2"
              :class="{
                'tw-grayscale':
                  studentStore.isHousemateGroupManageMode ||
                  studentStore.disableFilters,
              }"
            />
            <span
              class="tw-text-xs tw-font-semibold xl:tw-text-sm"
              :class="{
                'tw-text-[#5C6A75]':
                  studentStore.isHousemateGroupManageMode ||
                  studentStore.disableFilters,
              }"
              >{{ displayPrice }}</span
            >
          </div>
        </div>
        <div
          class="tw-flex tw-w-[165px] tw-flex-row tw-items-center tw-justify-center tw-rounded-full tw-border-2 tw-border-gray-200"
          :class="[
            desktopSearchInputHeightClasses,
            studentStore.isHousemateGroupManageMode ||
            studentStore.disableFilters
              ? 'tw-cursor-not-allowed tw-bg-gray-200'
              : [
                  'tw-cursor-pointer tw-bg-white',
                  desktopFilterAccentBorderClasses,
                ],
          ]"
          dusk="desktop-more-filters"
          data-testid="desktop-more-filters"
          @click="
            !(
              studentStore.isHousemateGroupManageMode ||
              studentStore.disableFilters
            ) && handleFilterModalOpen()
          "
        >
          <img
            :src="cdnAsset('svg/listings/filters.svg')"
            alt="Price icon"
            class="tw-ml-1 tw-mr-1 tw-h-4 tw-w-4 tw-items-center sm:tw-ml-2 md:tw-ml-1 lg:tw-ml-0 lg:tw-mr-1"
            :class="{
              'tw-grayscale':
                studentStore.isHousemateGroupManageMode ||
                studentStore.disableFilters,
            }"
          />
          <span
            class="tw-text-xs tw-font-semibold md:tw-w-[3rem] lg:tw-w-auto lg:tw-text-sm"
            :class="{
              'tw-text-[#5C6A75]':
                studentStore.isHousemateGroupManageMode ||
                studentStore.disableFilters,
            }"
            >More filters</span
          >
        </div>
      </div>
    </div>

    <PropertyTypeFiltersExtension
      v-if="enablePropertyTypeFilters"
      :break-out-of-container="false"
      class="tw-hidden lg:tw-block"
      :active="currentType"
      :expanded="propertyTypeFiltersExpanded"
      :enable-private-halls-filter="enablePrivateHallsFilter"
      :enable-spare-room-functionality="enableSpareRoomFunctionality"
      :disabled="
        studentStore.isHousemateGroupManageMode || studentStore.disableFilters
      "
      @update:expanded="propertyTypeFiltersExpanded = $event"
      @change="handleInlinePropertyTypeChange"
    />
  </div>
</template>

<script>
import { nextTick } from 'vue';
import PropertiesSearchFilterModal from '@components/PropertiesSearchFilterModal.vue';
import ExpandedPropertiesSearchFilterModal from '@components/ExpandedPropertiesSearchFilterModal.vue';
import MobileSearchStackedFiltersModal from '@components/MobileSearchStackedFiltersModal.vue';
import PropertyTypeFiltersExtension from '@components/Property/MobileSearchFilters/PropertyTypeFiltersExtension.vue';
import PropertyTypeFiltersTrigger from '@components/Property/MobileSearchFilters/PropertyTypeFiltersTrigger.vue';
import { useMapStore } from '@js/stores/map/mapStore';
import { getCurrentPath } from '@js/utilities';
import { useStudentStore } from '@js/stores/student/student';

export default {
  components: {
    PropertiesSearchFilterModal,
    ExpandedPropertiesSearchFilterModal,
    MobileSearchStackedFiltersModal,
    PropertyTypeFiltersExtension,
    PropertyTypeFiltersTrigger,
  },
  props: {
    enableMarketplaceGridSplitMaps: {
      default: false,
      type: Boolean,
    },
    shortlistModeActive: {
      default: false,
      type: Boolean,
    },
    areas: {
      default: function () {
        return [];
      },
      type: Array,
    },
    cities: {
      default: function () {
        return [];
      },
      type: Array,
    },
    currentCitySlug: {
      default: null,
      type: String,
    },
    currentLandlord: {
      default: null,
      type: [Object, String],
    },
    currentUniversitySlug: {
      default: null,
      type: String,
    },
    currentAreaSlug: {
      default: null,
      type: String,
    },
    currentBedrooms: {
      default: null,
      type: [Number, String],
    },
    currentBathrooms: {
      default: null,
      type: [Number, String],
    },
    currentType: {
      default: null,
      type: [String, Array],
    },
    currentMaxPrice: {
      default: null,
      type: [Number, String],
    },
    currentMinPrice: {
      default: null,
      type: [Number, String],
    },
    currentExpandedProperties: {
      default: false,
      type: Boolean,
    },
    currentEnquiredProperties: {
      type: Boolean,
      default: false,
    },
    maxCheapestRoomPrice: {
      default: null,
      type: [Number, String],
    },
    maxPriceSelected: {
      default: false,
      type: Boolean,
    },
    minCheapestRoomPrice: {
      default: null,
      type: [Number, String],
    },
    landlordQuery: {
      default: null,
      type: [Number, String],
    },
    propertyCount: {
      default: 0,
      type: Number,
    },
    reloading: {
      default: false,
      type: Boolean,
    },
    rooms: {
      default: function () {
        return [];
      },
      type: Array,
    },
    types: {
      default: function () {
        return [];
      },
      type: Array,
    },
    universities: {
      default: function () {
        return [];
      },
      type: Array,
    },
    enablePrivateHallsFilter: {
      default: false,
      type: Boolean,
    },
    enableSpareRoomFunctionality: {
      default: false,
      type: Boolean,
    },
    enablePropertyTypeFilters: {
      default: false,
      type: Boolean,
    },
    enableLocalFilterCounts: {
      default: false,
      type: Boolean,
    },
    enableStudentEnquiriesCta: {
      type: Boolean,
      default: false,
    },
    useExactRoomCounts: {
      type: Boolean,
      default: false,
    },
    hasEnquiredProperties: {
      type: Boolean,
      default: false,
    },
    resultsCountLoading: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    const mapStore = useMapStore();
    const studentStore = useStudentStore();

    return {
      studentStore,
      mapStore,
      showModal: false,
      showFilterModal: false,
      showMobileModal: false,
      shouldShowAreaDropdown: false,
      universityBlurTimer: null,
      selectedBedrooms: this.currentBedrooms ?? 0,
      selectedBathrooms: this.currentBathrooms ?? 0,
      selectedMaxPrice: this.currentMaxPrice
        ? this.currentMaxPrice
        : this.maxCheapestRoomPrice,
      setMaxPrice: this.currentMaxPrice
        ? this.currentMaxPrice
        : this.maxCheapestRoomPrice,
      searchFilterWidth: 0,
      propertyTypeFiltersExpanded: true,
    };
  },
  computed: {
    shortlistReturnUrl() {
      const url = new URL(window.location.href);
      url.searchParams.delete('tab');
      url.searchParams.delete('shortlistHash');

      return `${url.pathname}${url.search}${url.hash}`;
    },
    appliedFiltersCount() {
      let count = 0;

      // Price filter (min or max price set)
      if (this.currentMinPrice || this.currentMaxPrice) count++;

      // Bedrooms filter
      if (this.currentBedrooms) count++;

      // Bathrooms filter
      if (this.currentBathrooms) count++;

      // Property Type filter
      if (this.currentType) count++;

      // Area filter
      if (this.currentAreaSlug) count++;

      // University filter
      if (this.currentUniversitySlug) count++;

      return count;
    },
    currentTypeAsString() {
      if (!this.currentType) return null;
      if (Array.isArray(this.currentType)) {
        return this.currentType.join(',');
      }
      return this.currentType;
    },
    desktopFilterAccentBorderClasses() {
      return this.enablePropertyTypeFilters
        ? 'hover:tw-border-2 hover:tw-border-[#0082B8] focus:tw-border-2 focus:tw-border-[#0082B8]'
        : 'hover:tw-border-2 hover:tw-border-[#00A2E1] focus:tw-border-2 focus:tw-border-[#00A2E1]';
    },
    desktopSearchInputHeightClasses() {
      return this.enablePropertyTypeFilters ? 'lg:tw-h-[52px]' : 'lg:tw-h-12';
    },
    desktopFilterAccentDropdownClasses() {
      return this.enablePropertyTypeFilters
        ? 'hover:tw-bg-[#0082B8] focus:tw-bg-[#0082B8]'
        : 'hover:tw-bg-[#00A2E1] focus:tw-bg-[#00A2E1]';
    },
    displayPrice() {
      const maxPrice = this.currentMaxPrice;

      if (maxPrice && typeof maxPrice === 'string') {
        const intValue = parseInt(maxPrice, 10);
        return isNaN(intValue) ? 'Any' : `£${intValue}`;
      }

      if (maxPrice && typeof maxPrice === 'number') {
        return `£${maxPrice}`;
      }

      return 'Any';
    },
    computedsearchFilterWidthStyle() {
      return { width: `${this.searchFilterWidth}px` };
    },
    currentArea() {
      let scope = this;
      return scope.currentAreaSlug
        ? this.areas.filter(function (area) {
            return area.slug === scope.currentAreaSlug;
          })[0]
        : null;
    },
    currentCity() {
      let scope = this;
      return scope.currentCitySlug
        ? this.cities.filter(function (city) {
            return city.id === scope.currentCitySlug;
          })[0]
        : null;
    },
    currentMaxPriceString() {
      return this.maxCheapestRoomPrice
        ? 'Max: &pound;' + this.maxCheapestRoomPrice
        : false;
    },
    currentMaxPriceIsInvalid() {
      return this.selectedMaxPrice
        ? /\d/.test(this.selectedMaxPrice) === false
        : false;
    },
    currentMaxCheapestRoomPrice() {
      return parseInt(this.maxCheapestRoomPrice, 10);
    },
    currentMinCheapestRoomPrice() {
      return parseInt(this.minCheapestRoomPrice, 10);
    },
    currentUniversity() {
      let scope = this;
      return scope.currentUniversitySlug
        ? this.universities.filter(function (university) {
            return university.slug === scope.currentUniversitySlug;
          })[0]
        : null;
    },
    allUniversities() {
      return this.universities || [];
    },

    mapIsOpen() {
      return this.mapStore.mapIsOpen;
    },
    canShowMapView() {
      const currentPath = this.getCurrentPath();
      return currentPath && currentPath !== '/student-accommodation';
    },
    canShowGridSplitMaps() {
      return Boolean(
        this.canShowMapView && this.enableMarketplaceGridSplitMaps
      );
    },
  },
  watch: {
    propertyTypeFiltersExpanded(isExpanded) {
      this.syncDesktopPropertyFiltersState(isExpanded);
    },
    currentBedrooms(newVal) {
      this.selectedBedrooms =
        newVal === null || newVal === undefined || newVal === '' ? 0 : newVal;
    },
    currentBathrooms(newVal) {
      this.selectedBathrooms =
        newVal === null || newVal === undefined || newVal === '' ? 0 : newVal;
    },
    'mapStore.selectedUniversity': {
      handler(newUniversity) {
        // When university is selected in the map, update the filters
        if (newUniversity) {
          // Find the matching university in the universities array by ID
          const foundUniversity = this.universities.find(
            (u) => String(u.id) === String(newUniversity.id)
          );

          if (foundUniversity && foundUniversity.slug) {
            // Only emit if it's different from current selection to avoid loops
            if (this.currentUniversitySlug !== foundUniversity.slug) {
              this.$emit('set-current-university-slug', foundUniversity.slug);
            }
          }
        } else {
          // University was cleared in the map, clear it in filters too
          if (this.currentUniversitySlug) {
            this.$emit('set-current-university-slug', null);
          }
        }
      },
    },
  },
  mounted() {
    this.updateWidth();
    window.addEventListener('resize', this.updateWidth);
    this.syncDesktopPropertyFiltersState(this.propertyTypeFiltersExpanded);
    // Initialize map store with current university if one is selected
    if (this.currentUniversity) {
      this.mapStore.setSelectedUniversity(this.currentUniversity);
    }
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.updateWidth);
  },
  methods: {
    getCurrentPath,
    updateWidth() {
      nextTick(() => {
        const element = this.$refs.searchLocationInputFilter;

        if (element) {
          this.searchFilterWidth = element.offsetWidth;
        }
      });
    },
    displayFilter(value, defaultText = 'Any', symbol = '') {
      if (!value) {
        return defaultText;
      }

      if (typeof value === 'string') {
        const intValue = parseInt(value, 10);
        return isNaN(intValue) ? defaultText : intValue + symbol;
      }
      return typeof value === 'number' ? value + symbol : defaultText;
    },
    handleUniversitySelection(university) {
      this.shouldShowAreaDropdown = false;
      this.$emit('set-current-university-slug', university?.slug || null);
      // Set university in map store for travel times
      if (university && university.id) {
        this.mapStore.setSelectedUniversity(university);
      } else {
        this.mapStore.setSelectedUniversity(null);
      }
    },
    handleUniversityHide(event) {
      if (this.universityBlurTimer) {
        clearTimeout(this.universityBlurTimer);
      }
      this.universityBlurTimer = setTimeout(() => {
        // Ensure the dropdown is not open before hiding
        if (
          !this.$refs.searchLocationInputFilter.contains(event.relatedTarget)
        ) {
          this.shouldShowAreaDropdown = false;
        }
      }, 150);
    },
    handleUniversityFocus() {
      if (this.universityBlurTimer) {
        clearTimeout(this.universityBlurTimer);
      }
    },
    handleMinPriceChange(value) {
      this.selectedMinPrice = parseInt(value, 10);
      this.$emit('set-current-min-price', value);
      this.$emit('set-min-price-selected', true);
    },
    handleMaxPriceChange(value) {
      let parsed = value;

      if (typeof parsed === 'string') {
        parsed = parseInt(parsed);
      }

      this.selectedMaxPrice = parsed;
      this.$emit('set-current-max-price', parsed);
      this.$emit('set-max-price-selected', true);
    },
    handleCurrentBedroomsChange(value) {
      this.selectedBedrooms = value;
      this.$emit('set-current-bedrooms', value);
    },
    handleCurrentBathroomsChange(value) {
      this.selectedBathrooms = value;
      this.$emit('set-current-bathrooms', value);
    },
    syncDesktopPropertyFiltersState(isExpanded) {
      if (!this.enablePropertyTypeFilters) {
        this.$emit('desktop-property-filters-close');
        return;
      }

      this.$emit(
        isExpanded
          ? 'desktop-property-filters-open'
          : 'desktop-property-filters-close'
      );
    },
    handleModalClose() {
      this.showModal = false;
      this.$emit('filter-modal-close');
    },
    handleModalOpen() {
      this.shouldShowAreaDropdown = false;
      this.showModal = true;
    },
    handleFilterModalOpen() {
      this.shouldShowAreaDropdown = false;
      this.showFilterModal = true;
    },
    handleFilterModalClose() {
      this.showFilterModal = false;
      this.$emit('filter-modal-close');
    },
    handleMobileModalOpen() {
      // prevent double scroll when modal is active
      document.body.classList.add('tw-overflow-hidden');

      this.shouldShowAreaDropdown = false;
      this.showMobileModal = true;
    },
    handleMobileModalClose() {
      this.showMobileModal = false;
      this.$emit('filter-modal-close');
    },
    handlePropertyTypeChange(propertyType) {
      this.$emit('set-current-type', propertyType);
    },
    handleInlinePropertyTypeChange(propertyType) {
      this.$emit('apply-inline-property-type-filter', propertyType);
    },
    handleAreaSelection(area) {
      this.$emit('set-current-area-slug', area?.slug || null);

      this.triggerLocationUpdate(area);
    },
    triggerLocationUpdate(area) {
      const locationChangedEvent = new CustomEvent('locationChanged', {
        detail: {
          city: this.currentCitySlug,
          area: area?.slug,
        },
      });

      document.dispatchEvent(locationChangedEvent);
    },
    openMapAndScroll() {
      this.$emit('toggle-map-is-open');
      this.$nextTick(() => {
        window.scrollTo({ top: 0 });
      });
    },
    handleEnquiredPropertiesChange(checked) {
      this.$emit('set-enquired-properties', checked);
    },
  },
};
</script>

<style scoped>
@media (max-width: 1023px) {
  .search-filter-select-refresh,
  .search-filter-select-refresh-inner {
    max-width: calc(100vw - 32px);
  }
}

/* Hide scrollbar while keeping scrolling functionality */
.hide-scrollbar {
  /* For Webkit browsers (Chrome, Safari, Edge) */
  scrollbar-width: none; /* Firefox */
  -ms-overflow-style: none; /* Internet Explorer 10+ */
}

.hide-scrollbar::-webkit-scrollbar {
  display: none; /* Webkit browsers */
}

/* Filter count badge */
.filter-button-with-count {
  position: relative;
  min-width: 55px;
  min-height: 55px;
}

.filter-button-with-count::after {
  content: attr(data-filter-count);
  position: absolute;
  top: 0;
  right: 0;
  background: #f48831;
  color: white;
  border-radius: 50%;
  width: 16px;
  height: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 11px;
  font-weight: 600;
  line-height: 16px;
}
</style>
